import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  FormControlLabel,
  styled,
  FormLabel,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Invitation,
  InvitationState,
  UpdateInvitationRequest,
} from '../../../../redux/invitations/invitation.model';
import { getPolyglot } from '../../../../i18n';
import moment from 'moment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { getStatusColor, StyledChip } from './components/StyledChip';
import { NormalizedGroup } from '../../../../redux/groups/api/GroupAdapter';
import { findMatchingCompany } from '../../util/invitationsUtilFunctions';
import { ErrorText } from '../../../../theme/components/Forms';

interface InvitationRequestModalProps {
  open: boolean;
  invitation: Invitation | null;
  onClose: () => void;
  onReject: (invitation: Invitation) => void;
  onApprove: (invitation: Partial<UpdateInvitationRequest>) => void;
  availableCompanies: NormalizedGroup[];
  isLoading: boolean;
}

const validationSchema = () =>
  Yup.object().shape({
    name: Yup.string(),
    email: Yup.string().required('freshwatch.invitation.modal.required_email'),
    companyName: Yup.string().required(
      'freshwatch.invitation.modal.required_company_name'
    ),
  });

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  display: 'block',
  color: theme.palette.text.primary,
}));

export const InvitationRequestModal: React.FC<InvitationRequestModalProps> = ({
  open,
  invitation,
  onClose,
  onReject,
  onApprove,
  availableCompanies = [],
  isLoading,
}) => {
  const polyglot = getPolyglot();
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >(undefined);
  const readMode = [InvitationState.APPROVED, InvitationState.SENT].includes(
    invitation?.status || InvitationState.NEW
  );
  const isRejected = invitation?.status === InvitationState.REJECTED;

  const formik = useFormik({
    initialValues: {
      name: invitation?.user?.name || '',
      email: invitation?.user?.email || '',
      companyName: invitation?.companyName || '',
      createCompany: false,
    },
    validationSchema: validationSchema(),
    onSubmit: async (values) => {
      formik.setTouched({
        name: true,
        email: true,
        companyName: true,
      });
      if (!formik.isValid) return;
      handleApprove();
    },
  });
  React.useEffect(() => {
    setSelectedCompanyId(undefined);
    if (invitation?.companyGroupId) {
      // If invitation already has a company group assigned, use it
      setSelectedCompanyId(invitation.companyGroupId);
    } else if (invitation?.companyName) {
      // Otherwise, try to find a matching company based on the company name
      const matchingCompany = findMatchingCompany(
        invitation.companyName,
        availableCompanies
      );
      if (matchingCompany) {
        setSelectedCompanyId(matchingCompany.id);
      }
      formik.setFieldValue('companyName', invitation?.companyName || '');
    }
  }, [invitation, availableCompanies]);

  if (!invitation) return null;

  const handleReject = () => {
    onReject(invitation);
  };

  const handleApprove = () => {
    onApprove({
      ...invitation,
      status: InvitationState.APPROVED,
      companyGroupId: selectedCompanyId,
      companyGroupName: formik.values.companyName,
      companyName: formik.values.companyName,
    });
  };

  const formatDate = (date: number): string => {
    if (!date) return '-';
    return moment(date).format('YYYY/MM/DD');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ padding: '2rem 0' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography variant="h2" gutterBottom>
              {polyglot.t('freshwatch.invitation.modal.title')}
            </Typography>
            <StyledChip
              sx={{
                ...getStatusColor(invitation.status),
                width: 'fit-content',
                height: '2rem',
              }}
            >
              {polyglot.t(`freshwatch.invitation.detail.${invitation.status}`)}
            </StyledChip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {polyglot.t('freshwatch.invitation.modal.request_received_at', {
              date: formatDate(invitation.createdAt),
            })}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ padding: 2 }}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={1}>
            {/* Name Field */}
            <StyledFormLabel htmlFor="name">
              {polyglot.t('freshwatch.invitation.detail.name')}
            </StyledFormLabel>
            <TextField
              fullWidth
              id={'name'}
              value={invitation?.user?.name || ''}
              disabled
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                },
              }}
            />

            {/* Email Field */}
            <StyledFormLabel htmlFor="email">
              {polyglot.t('freshwatch.invitation.detail.email')}
            </StyledFormLabel>

            <TextField
              fullWidth
              value={invitation.email || ''}
              disabled
              id={'email'}
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: (theme) => theme.palette.text.secondary,
                },
              }}
            />

            {/* Companies Field */}
            {readMode ? null : (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.createCompany}
                    onChange={() => {
                      formik.setFieldValue(
                        'createCompany',
                        !formik.values.createCompany
                      );
                      if (!formik.values.createCompany) {
                        formik.setFieldValue(
                          'companyName',
                          invitation?.companyName || ''
                        );
                      }
                      setSelectedCompanyId(undefined);
                    }}
                    color="primary"
                    id="createCompany"
                  />
                }
                label={
                  <Typography
                    variant={'caption'}
                    color={'text.secondary'}
                    sx={{ my: 1 }}
                  >
                    {polyglot.t(
                      'freshwatch.invitation.modal.no_matching_company'
                    )}
                  </Typography>
                }
              />
            )}
            {(formik.values.createCompany && !readMode) ||
            (readMode && !selectedCompanyId) ? (
              <>
                <StyledFormLabel htmlFor="companies">
                  {polyglot.t('freshwatch.invitation.detail.company_name')}
                </StyledFormLabel>
                <TextField
                  fullWidth
                  id={'companyName'}
                  onChange={formik.handleChange}
                  value={formik.values.companyName}
                  onBlur={formik.handleBlur}
                  disabled={readMode}
                  error={Boolean(
                    formik.touched.companyName && formik.errors.companyName
                  )}
                  helperText={
                    Boolean(
                      formik.touched.companyName && formik.errors.companyName
                    ) && (
                      <ErrorText>
                        {polyglot.t(
                          'freshwatch.invitation.modal.required_company_name'
                        )}
                      </ErrorText>
                    )
                  }
                />
              </>
            ) : null}

            {(readMode && selectedCompanyId) ||
            (!readMode && !formik.values.createCompany) ? (
              <>
                <StyledFormLabel htmlFor="companiesList">
                  {polyglot.t('freshwatch.invitation.modal.companies_list')}
                </StyledFormLabel>
                <TextField
                  fullWidth
                  select
                  value={selectedCompanyId || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedCompanyId(id);
                    const selectedCompany = Object.values(
                      availableCompanies
                    ).find((company) => company.id === id);
                    formik.setFieldValue(
                      'companyName',
                      selectedCompany ? selectedCompany.name : ''
                    );
                  }}
                  disabled={readMode}
                  SelectProps={{ displayEmpty: true }}
                  placeholder={polyglot.t(
                    'freshwatch.invitation.modal.select_company'
                  )}
                  id={'companiesList'}
                  sx={{ mb: 2 }}
                  helperText={
                    !selectedCompanyId && (
                      <ErrorText>
                        {polyglot.t(
                          'freshwatch.invitation.modal.required_company_name'
                        )}
                      </ErrorText>
                    )
                  }
                >
                  <MenuItem value="">
                    <em>
                      {polyglot.t('freshwatch.invitation.modal.no_company')}
                    </em>
                  </MenuItem>
                  {Object.values(availableCompanies).map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            ) : null}
          </Stack>
          {/* Action Buttons */}

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              p: '2rem 1rem 0 1rem',
              justifyContent: 'space-between',
            }}
          >
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              {polyglot.t('freshwatch.invitation.modal.back_to_list')}
            </Button>

            {(!readMode && !isRejected) ||
            invitation.status === InvitationState.SENT ? (
              <Button
                variant="outlined"
                onClick={handleReject}
                color="error"
                disabled={isLoading}
              >
                {polyglot.t('freshwatch.invitation.modal.reject')}
              </Button>
            ) : null}

            {readMode ? null : (
              <Button
                variant="contained"
                onClick={handleApprove}
                disabled={
                  (!selectedCompanyId && !formik.values.createCompany) ||
                  isLoading
                }
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  polyglot.t(
                    isRejected
                      ? 'freshwatch.invitation.modal.approve_anyway'
                      : 'freshwatch.invitation.modal.save_and_approve'
                  )
                )}
              </Button>
            )}
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InvitationRequestModal;
